/**
 * Motor de regras do Tigrinho Tour (regras do Business Tour).
 *
 *   createGame(config)                → { state, events }
 *   applyAction(state, action, nowMs) → { state, events }
 *
 * `nowMs` é o tempo de partida decorrido (ms) informado pelo servidor.
 * O motor é determinístico: mesma semente + mesmas ações + mesmos tempos = mesma partida.
 */
import { BASE_DECK, CAOS_DECK, CARD_BY_ID, applyCardChoice, resolveCard } from './cards';
import {
  type Ctx,
  charge,
  credit,
  emit,
  leaveJail,
  moveForward,
  moveForwardTo,
  sendToJail,
  setOwner,
  transfer,
  useHeld,
  touch,
  handSize,
  hasCard,
} from './core';
import {
  CITY_SQUARES,
  GROUPS,
  OWNABLE_SQUARES,
  RESORT_SQUARES,
  isOwnable,
  levelCost,
  square,
  upgradeCost,
  valueOf,
} from './board';
import {
  activePlayers,
  completeGroupsOf,
  completeSidesOf,
  currentPlayer,
  getPlayer,
  ownedBy,
  propertyValue,
  rankByWorth,
  resortCount,
} from './queries';
import { maxRentCharged, maxRentOnBoard, rentFor } from './rent';
import {
  BOARD_SIZE,
  CHAMPIONSHIP_MAX_MULT,
  CHAMPIONSHIP_START_MULT,
  FACADA_CASH_SHARE,
  FACADA_MIN,
  FESTIVAL_COUNT,
  HAND_LIMIT,
  HOTEL_LEVEL,
  JAIL_FINE,
  MAX_JAIL_ATTEMPTS,
  MAX_LEVEL_BEFORE_HOTEL,
  MAX_PLAYERS,
  MIN_PLAYERS,
  MODES,
  RULES_VERSION,
  SELL_RATE,
  START_CASH,
  CLOVER_CASH_SHARE,
  CLOVER_CHANCE,
  CLOVER_PEEK,
  UNDERDOG_MIN_AVG,
  UNDERDOG_SHARE,
  MAGNET_CHANCE,
  EVADE_FAIL_MULT,
  EVADE_JAIL_ATTEMPTS,
  EVADE_JAIL_FINE_MULT,
  EVADE_MIN_TOTAL,
  EVADE_SUCCESS_SHARE,
  MST_EXPEL_COST,
  SHOP,
  MST_IDLE_ROUNDS,
  TAKEOVER_MULT,
  TAX_RATE,
  TENSION_ACCEL,
  TENSION_MAX_ACCEL,
  TENSION_REF_MS,
  TENSION_THRESHOLDS,
  WORLD_TOUR_COST,
} from './rules';
import { nextFloat, nextInt, shuffle } from './rng';
import {
  type Action,
  type ApplyResult,
  EngineError,
  type GameConfig,
  type GameState,
  type HeldCard,
  type Level,
  type Player,
  type PlayerId,
  type Prompt,
  type Roll,
  type Step,
  type TradeOffer,
  type WinReason,
} from './types';

const fail = (code: string, message: string): never => {
  throw new EngineError(code, message);
};

// ===========================================================================
// Criação da partida

export function createGame(config: GameConfig): ApplyResult {
  const n = config.players.length;
  if (n < MIN_PLAYERS || n > MAX_PLAYERS) fail('player_count', `A partida precisa de ${MIN_PLAYERS} a ${MAX_PLAYERS} jogadores`);
  const ids = new Set(config.players.map((p) => p.id));
  if (ids.size !== n) fail('duplicate_player', 'Jogadores repetidos');
  const mode = MODES[config.mode];
  if (!mode) fail('mode', `Modo desconhecido: ${config.mode}`);

  const s: GameState = {
    rulesVersion: RULES_VERSION,
    mode: config.mode,
    durationMs: mode.durationMs,
    players: [],
    props: Array.from({ length: BOARD_SIZE }, () => null),
    festivals: [],
    championship: null,
    current: 0,
    turnCounter: 0,
    round: 1,
    phase: 'roll',
    doublesInRow: 0,
    extraRoll: false,
    lastRoll: null,
    queue: [],
    prompt: null,
    debts: [],
    deck: [],
    discard: [],
    pendingCard: null,
    caosAdded: false,
    effects: [],
    attackedRound: {},
    tension: 1,
    bankruptcyHappened: false,
    firstHotelDone: false,
    elapsedMs: 0,
    roundStarts: [0],
    finalRound: false,
    rng: config.seed >>> 0,
    trades: [],
    nextTradeId: 1,
    allowForcedDice: config.allowForcedDice === true,
    winners: [],
    winReason: null,
  };
  const ctx: Ctx = { s, ev: [] };

  // Ordem de jogada sorteada ("Sorteando quem começa…"); todos com o mesmo dinheiro.
  const order = shuffle(s, config.players);
  const bonus = config.seatBonus ?? [];
  s.players = order.map(
    (p, seat): Player => ({
      id: p.id,
      name: p.name,
      seat,
      cash: START_CASH + (bonus[seat] ?? 0),
      position: 0,
      inJail: false,
      jailAttempts: 0,
      held: [],
      laps: 0,
      worldTour: false,
      eliminated: false,
      stats: {
        rentPaid: 0,
        rentReceived: 0,
        biggestRentPaid: 0,
        attacksMade: 0,
        attacksReceived: 0,
        takeoversMade: 0,
        takeoversSuffered: 0,
        cardsDrawn: [],
      },
    }),
  );
  s.deck = shuffle(s, BASE_DECK);
  // Festivais: algumas cidades começam com aluguel em dobro.
  if (!config.noFestivals) s.festivals = shuffle(s, CITY_SQUARES).slice(0, FESTIVAL_COUNT).sort((a, b) => a - b);
  emit(ctx, { type: 'gameStarted', mode: s.mode, order: s.players.map((p) => p.id), festivals: [...s.festivals] });
  emit(ctx, { type: 'turnStarted', player: s.players[0]!.id, turn: 0, round: 1 });
  return { state: s, events: ctx.ev };
}

// ===========================================================================
// Aplicação de ações

export interface ApplyOptions {
  /** Altera o estado recebido em vez de copiar (usado pelo simulador, por velocidade). */
  mutate?: boolean;
}

export function applyAction(state: GameState, action: Action, nowMs: number, opts: ApplyOptions = {}): ApplyResult {
  if (state.phase === 'finished') fail('game_over', 'A partida já terminou');
  const s = opts.mutate ? state : structuredClone(state);
  const ctx: Ctx = { s, ev: [] };

  updateClock(ctx, nowMs);
  handle(ctx, action);
  advance(ctx);
  checkWinners(ctx);
  updateTension(ctx);
  return { state: s, events: ctx.ev };
}

function handle(ctx: Ctx, a: Action): void {
  switch (a.type) {
    case 'TICK':
      return;
    case 'ROLL':
      return onRoll(ctx, a.player, a.dice);
    case 'PAY_JAIL':
      return onPayJail(ctx, a.player);
    case 'PAY_TAX':
      return onPayTax(ctx, a.player);
    case 'BUY_CARD':
      return onBuyCard(ctx, a.player, a.card);
    case 'EXPEL_MST':
      return onExpelMst(ctx, a.player);
    case 'ACCEPT_MST':
      return onAcceptMst(ctx, a.player);
    case 'EVADE_TAX':
      return onEvadeTax(ctx, a.player, a.dice);
    case 'USE_JAIL_CARD':
      return onUseJailCard(ctx, a.player);
    case 'BUILD':
      return onBuild(ctx, a.player, a.level);
    case 'SKIP_BUILD':
      return onSkipBuild(ctx, a.player);
    case 'TAKEOVER':
      return onTakeover(ctx, a.player);
    case 'SKIP_TAKEOVER':
      return onSkipTakeover(ctx, a.player);
    case 'HOST':
      return onHost(ctx, a.player, a.square);
    case 'TOUR':
      return onTour(ctx, a.player, a.to);
    case 'SKIP_TOUR':
      return onSkipTour(ctx, a.player);
    case 'CHOOSE':
      return onChoose(ctx, a.player, a.choice);
    case 'SELL':
      return onSell(ctx, a.player, a.square);
    case 'PAY_DEBT':
      return onPayDebt(ctx, a.player);
    case 'DECLARE_BANKRUPTCY':
      return onBankruptcy(ctx, a.player);
    case 'AUTO_SETTLE_DEBT':
      return onAutoSettle(ctx, a.player);
    case 'PROPOSE_TRADE':
      return onProposeTrade(ctx, a.player, a.to, a.give, a.get);
    case 'ACCEPT_TRADE':
      return onAcceptTrade(ctx, a.player, a.tradeId);
    case 'REJECT_TRADE':
      return onRejectTrade(ctx, a.player, a.tradeId);
    case 'CANCEL_TRADE':
      return onCancelTrade(ctx, a.player, a.tradeId);
    case 'END_TURN':
      return onEndTurn(ctx, a.player);
    default: {
      const never: never = a;
      fail('unknown_action', `Ação desconhecida: ${JSON.stringify(never)}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Relógio, tensão e fim

function updateClock(ctx: Ctx, nowMs: number): void {
  const s = ctx.s;
  if (Number.isFinite(nowMs) && nowMs > s.elapsedMs) s.elapsedMs = nowMs;
  if (!s.finalRound && s.elapsedMs >= s.durationMs) {
    s.finalRound = true;
    emit(ctx, { type: 'finalRoundStarted' });
  }
}

export function computeTension(s: GameState): 1 | 2 | 3 | 4 {
  // O relógio manda; perigo real e a primeira quebra adiantam a tensão (até +15% do tempo).
  const maxRent = maxRentOnBoard(s);
  const danger = maxRent > 0 && activePlayers(s).some((p) => p.cash < maxRent);
  const accel = Math.min(TENSION_MAX_ACCEL, (danger ? TENSION_ACCEL : 0) + (s.bankruptcyHappened ? TENSION_ACCEL : 0));
  // No Business Tour a partida quase sempre acaba antes do relógio (vitória na hora ou
  // eliminação, perto dos 20 min). Por isso a tensão corre num relógio de no máximo 20 min.
  const frac = s.elapsedMs / Math.min(s.durationMs, TENSION_REF_MS) + accel;
  let level = 1;
  TENSION_THRESHOLDS.forEach((t, i) => {
    if (frac >= t) level = i + 1;
  });
  return Math.min(4, Math.max(s.tension, level)) as 1 | 2 | 3 | 4;
}

function updateTension(ctx: Ctx): void {
  const s = ctx.s;
  if (s.phase === 'finished') return;
  const level = computeTension(s);
  if (level !== s.tension) {
    s.tension = level;
    emit(ctx, { type: 'tensionChanged', level });
  }
  if (s.tension >= 3 && !s.caosAdded) {
    s.caosAdded = true;
    s.deck = shuffle(s, [...s.deck, ...CAOS_DECK]);
    emit(ctx, { type: 'caosAdded' });
  }
}

/** Duração média das últimas rodadas completas (até 3). */
export function recentRoundMs(s: GameState): number {
  const starts = s.roundStarts;
  const diffs: number[] = [];
  for (let i = starts.length - 1; i > 0 && diffs.length < 3; i--) diffs.push(starts[i]! - starts[i - 1]!);
  return diffs.length ? diffs.reduce((a, b) => a + b, 0) / diffs.length : 0;
}

/**
 * No início de cada rodada: se o tempo que falta for até 1,5 rodada, esta é a última.
 * Assim a partida termina na rodada cujo fim fica mais perto do relógio.
 */
function maybeStartFinalRound(ctx: Ctx): void {
  const s = ctx.s;
  if (s.finalRound) return;
  const avg = recentRoundMs(s);
  const remaining = s.durationMs - s.elapsedMs;
  if (avg > 0 && remaining <= avg * 1.5) {
    s.finalRound = true;
    emit(ctx, { type: 'finalRoundStarted' });
  }
}

function endGame(ctx: Ctx, reason: WinReason, winners?: PlayerId[]): void {
  const s = ctx.s;
  const ranking = rankByWorth(s);
  if (winners) s.winners = winners;
  else {
    const top = ranking[0]!.worth;
    s.winners = ranking.filter((r) => r.worth === top && !getPlayer(s, r.player).eliminated).map((r) => r.player);
  }
  s.winReason = reason;
  s.phase = 'finished';
  s.prompt = null;
  s.queue = [];
  s.debts = [];
  emit(ctx, { type: 'gameEnded', winners: s.winners, reason, ranking });
}

/** Vitórias na hora: 3 grupos completos, um lado inteiro, os 4 resorts, ou último que sobrou. */
function checkWinners(ctx: Ctx): void {
  const s = ctx.s;
  if (s.phase === 'finished') return;
  const alive = activePlayers(s);
  if (alive.length === 1) return endGame(ctx, 'lastStanding', [alive[0]!.id]);
  if (s.debts.length > 0) return; // dívida em aberto: resolve antes de declarar vitória
  for (const p of alive) {
    if (completeGroupsOf(s, p.id).length >= 3) return endGame(ctx, 'triple', [p.id]);
    if (completeSidesOf(s, p.id).length >= 1) return endGame(ctx, 'line', [p.id]);
    if (resortCount(s, p.id) === RESORT_SQUARES.length) return endGame(ctx, 'resorts', [p.id]);
  }
}

// ---------------------------------------------------------------------------
// Loop de resolução

/** Executa os passos pendentes até precisar de uma decisão de alguém. */
function advance(ctx: Ctx): void {
  const s = ctx.s;
  for (let guard = 0; guard < 1000; guard++) {
    if (s.phase === 'finished') return;
    if (s.debts.length > 0 || s.prompt) return;
    const step = s.queue.shift();
    if (!step) {
      if (s.phase === 'resolving') {
        const p = currentPlayer(s);
        if (s.extraRoll && !p.inJail && !p.eliminated) {
          s.extraRoll = false;
          s.phase = 'roll';
        } else {
          s.extraRoll = false;
          s.phase = 'endTurn';
        }
      }
      return;
    }
    processStep(ctx, step);
  }
  throw new Error('Loop de resolução não terminou');
}

function processStep(ctx: Ctx, step: Step): void {
  if (getPlayer(ctx.s, step.player).eliminated) return;
  switch (step.k) {
    case 'land':
      return landOn(ctx, step.player);
    case 'magnet':
      return magnetCheck(ctx, step.player);
    case 'mst':
      return mstArrive(ctx, step.player, step.square);
    case 'passiveLand':
      return passiveLand(ctx, step.player);
    case 'draw':
      return drawCard(ctx, step.player);
    case 'offerTakeover':
      return offerTakeover(ctx, step.player, step.square);
    case 'offerBuild':
      return offerBuild(ctx, step.player, step.square);
  }
}

/** Nível máximo que o jogador pode ter nesta cidade se construir agora. */
export function maxLevelFor(s: GameState, playerId: PlayerId, index: number): Level {
  const sq = square(index);
  if (sq.kind === 'resort') return 0;
  const o = s.props[index];
  // Na compra dá para ir direto até a casa 3 (pedido do Matheus em 19/10).
  // Hotel só para quem já tem a casa 3 e cai de novo na cidade.
  if (o && o.owner === playerId && o.level >= MAX_LEVEL_BEFORE_HOTEL) return HOTEL_LEVEL;
  return MAX_LEVEL_BEFORE_HOTEL as Level;
}

function landOn(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const p = getPlayer(s, playerId);
  const pos = p.position;
  const sq = square(pos);
  switch (sq.kind) {
    case 'city':
    case 'resort': {
      const o = s.props[pos];
      if (!o || o.owner === playerId) {
        offerBuild(ctx, playerId, pos);
      } else {
        chargeRent(ctx, playerId, pos);
        // Depois de pagar, pode comprar a cidade do dono pelo dobro (não vale para hotel nem resort).
        s.queue.unshift({ k: 'offerTakeover', player: playerId, square: pos });
      }
      return;
    }
    case 'chance':
      s.queue.unshift({ k: 'draw', player: playerId });
      return;
    case 'tax': {
      // Faz o L: pagar o imposto ou arriscar a sonegação.
      const amount = Math.round(propertyValue(s, playerId) * TAX_RATE);
      if (amount > 0 && useHeld(ctx, playerId, 'contador')) {
        emit(ctx, { type: 'taxExempt', player: playerId, amount });
        return;
      }
      if (amount > 0)
        s.prompt = {
          kind: 'tax',
          player: playerId,
          square: pos,
          amount,
          evaded: Math.round(amount * EVADE_SUCCESS_SHARE),
          caught: amount * EVADE_FAIL_MULT,
        };
      return;
    }
    case 'island':
      sendToJail(ctx, playerId, 'ilha');
      return;
    case 'championship': {
      const options = ownedBy(s, playerId).filter((i) => square(i).kind === 'city');
      if (options.length > 0) s.prompt = { kind: 'championship', player: playerId, options };
      return;
    }
    case 'worldTour':
      p.worldTour = true;
      emit(ctx, { type: 'worldTourReady', player: playerId });
      return;
    default:
      return;
  }
}

/** Quem foi levado pela "Troca de lugar" paga o que deve na casa nova, sem comprar nem puxar carta. */
function passiveLand(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const p = getPlayer(s, playerId);
  const sq = square(p.position);
  if (isOwnable(p.position)) {
    const o = s.props[p.position];
    if (o && o.owner !== playerId) chargeRent(ctx, playerId, p.position);
  } else if (sq.kind === 'tax') {
    const amount = Math.round(propertyValue(s, playerId) * TAX_RATE);
    if (amount > 0) charge(ctx, playerId, 'bank', amount, 'imposto');
  } else if (sq.kind === 'island') {
    sendToJail(ctx, playerId, 'ilha');
  }
}

function offerBuild(ctx: Ctx, playerId: PlayerId, index: number): void {
  const s = ctx.s;
  const p = getPlayer(s, playerId);
  const o = s.props[index];
  if (o && o.owner !== playerId) return;
  const from = o ? o.level : -1;
  const max = maxLevelFor(s, playerId, index);
  if (from >= max) return;
  // Só pergunta se dá para pagar ao menos o próximo nível (com Black Friday, metade).
  const next = levelCost(index, from + 1);
  const discount = hasCard(p, 'blackFriday') && from >= 0 ? Math.ceil(next / 2) : next;
  if (p.cash < discount) return;
  s.prompt = { kind: 'build', player: playerId, square: index, from, max };
}

function offerTakeover(ctx: Ctx, playerId: PlayerId, index: number): void {
  const s = ctx.s;
  const o = s.props[index];
  if (!o || o.owner === playerId || square(index).kind !== 'city' || o.level >= HOTEL_LEVEL) return;
  const price = takeoverPrice(s, index);
  if (getPlayer(s, playerId).cash < price) return;
  s.prompt = { kind: 'takeover', player: playerId, square: index, price };
}

export function takeoverPrice(s: GameState, index: number): number {
  const o = s.props[index];
  return o ? valueOf(index, o.level) * TAKEOVER_MULT : 0;
}

function chargeRent(ctx: Ctx, payer: PlayerId, index: number): void {
  const s = ctx.s;
  const o = s.props[index]!;
  const quote = rentFor(s, index, payer);
  if (quote.waived) {
    emit(ctx, { type: 'rentWaived', payer, square: index, reason: quote.waived });
    return;
  }
  let amount = quote.amount;
  if (amount <= 0) return;
  if (useHeld(ctx, payer, 'calote')) {
    emit(ctx, { type: 'rentWaived', payer, square: index, reason: 'calote' });
    return;
  }
  if (useHeld(ctx, payer, 'cupom')) amount = Math.ceil(amount / 2);
  const p = getPlayer(s, payer);
  const facada = amount >= FACADA_MIN && (amount >= p.cash * FACADA_CASH_SHARE || amount > maxRentCharged(s, payer));
  const mortal = amount > p.cash;
  p.stats.biggestRentPaid = Math.max(p.stats.biggestRentPaid, amount);
  emit(ctx, { type: 'rentPaid', payer, owner: o.owner, square: index, amount, facada, mortal });
  touch(s, index);
  charge(ctx, payer, o.owner, amount, 'aluguel', true);
}

/** Quanto a carta ajuda quem puxa: ataque > Legaaaal > Caos > F total. */
function cardScore(id: string): number {
  const c = CARD_BY_ID[id];
  if (!c) return 0;
  if (c.attack) return 3;
  return c.deck === 'sorte' ? 2 : c.deck === 'caos' ? 1 : 0;
}

/**
 * Trevo da Sorte de Malandro. Acende de dois jeitos (sempre com pelo menos uma propriedade):
 * - malandro: guarda em caixa ao menos metade do valor que já investiu;
 * - azarão: tem no máximo metade das propriedades da média dos outros (que têm 2 ou mais).
 * Usado pelo motor e pela tela (aceso no placar).
 */
export function cloverReason(s: Pick<GameState, 'players' | 'props'>, playerId: PlayerId): 'malandro' | 'azarao' | null {
  const p = s.players.find((x) => x.id === playerId);
  if (!p || p.eliminated) return null;
  let invested = 0;
  s.props.forEach((o, i) => {
    if (o && o.owner === playerId) invested += valueOf(i, o.level);
  });
  if (invested === 0) return null;
  if (p.cash >= invested * CLOVER_CASH_SHARE) return 'malandro';
  return isUnderdog(s, playerId) ? 'azarao' : null;
}

/** Azarão: tem propriedade, mas no máximo metade da média dos outros (que têm 2 ou mais). */
export function isUnderdog(s: Pick<GameState, 'players' | 'props'>, playerId: PlayerId): boolean {
  const p = s.players.find((x) => x.id === playerId);
  if (!p || p.eliminated) return false;
  const count: Record<string, number> = {};
  for (const o of s.props) if (o) count[o.owner] = (count[o.owner] ?? 0) + 1;
  const mine = count[playerId] ?? 0;
  if (mine === 0) return false;
  const others = s.players.filter((x) => x.id !== playerId && !x.eliminated);
  const avg = others.reduce((a, x) => a + (count[x.id] ?? 0), 0) / Math.max(1, others.length);
  return avg >= UNDERDOG_MIN_AVG && mine <= avg * UNDERDOG_SHARE;
}

/** Cidade com ímã: do azarão, e a casa logo antes dela puxa quem parar ali (50%). */
export function magnetSquares(s: Pick<GameState, 'players' | 'props'>): number[] {
  const out: number[] = [];
  s.props.forEach((o, i) => {
    if (o && square(i).kind === 'city' && isUnderdog(s, o.owner)) out.push(i);
  });
  return out;
}

/** Quem parou logo antes de uma cidade com ímã joga o dado especial. */
function magnetCheck(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const p = getPlayer(s, playerId);
  // Da Ilha ninguém é puxado (quem cai lá vai preso); de qualquer outra casa, sim.
  if (square(p.position).kind === 'island') return;
  const target = magnetTargetFrom(p.position);
  const o = s.props[target];
  if (!o || o.owner === playerId || square(target).kind !== 'city' || !isUnderdog(s, o.owner)) return;
  const pulled = nextFloat(s) < MAGNET_CHANCE;
  emit(ctx, { type: 'magnetRolled', player: playerId, from: p.position, square: target, owner: o.owner, pulled });
  if (!pulled) return;
  const from = p.position;
  p.position = target;
  emit(ctx, { type: 'moved', player: playerId, from, to: target, how: 'magnet' });
}

/**
 * Cidade que o ímã alcança a partir desta casa: a da frente. Se a da frente for a Ilha, o ímã
 * pula a Ilha (senão a cidade logo depois da Ilha nunca puxaria ninguém).
 */
export function magnetTargetFrom(pos: number): number {
  const next = (pos + 1) % BOARD_SIZE;
  return square(next).kind === 'island' ? (next + 1) % BOARD_SIZE : next;
}

export function luckyClover(s: Pick<GameState, 'players' | 'props'>, playerId: PlayerId): boolean {
  return cloverReason(s, playerId) !== null;
}

function drawCard(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  if (s.deck.length === 0) {
    s.deck = shuffle(s, s.discard);
    s.discard = [];
  }
  // Sorte de Malandro: com o trevo aceso, às vezes olha as cartas de cima e fica com a melhor.
  let pick = 0;
  const clover = luckyClover(s, playerId);
  if (clover && s.deck.length > 1 && nextFloat(s) < CLOVER_CHANCE) {
    const n = Math.min(CLOVER_PEEK, s.deck.length);
    for (let i = 1; i < n; i++) if (cardScore(s.deck[i]!) > cardScore(s.deck[pick]!)) pick = i;
  }
  const card = s.deck.splice(pick, 1)[0];
  if (!card) return; // todas as cartas estão nas mãos dos jogadores
  s.pendingCard = card;
  getPlayer(s, playerId).stats.cardsDrawn.push(card);
  emit(ctx, { type: 'cardDrawn', player: playerId, card, clover: pick > 0 });
  resolveCard(ctx, playerId, card);
}

// ---------------------------------------------------------------------------
// Permissões

function requireCurrent(s: GameState, player: PlayerId): Player {
  const p = currentPlayer(s);
  if (p.id !== player) fail('not_your_turn', 'Não é a sua vez');
  return p;
}

function requireFree(s: GameState): void {
  if (s.debts.length > 0) fail('debt_pending', 'Há uma dívida em aberto');
  if (s.prompt) fail('prompt_pending', 'Há uma decisão pendente');
}

function requirePrompt<K extends Prompt['kind']>(s: GameState, kind: K, player: PlayerId): Extract<Prompt, { kind: K }> {
  if (s.debts.length > 0) fail('debt_pending', 'Há uma dívida em aberto');
  if (!s.prompt || s.prompt.kind !== kind) fail('wrong_prompt', 'Não há essa decisão agora');
  const pr = s.prompt as Extract<Prompt, { kind: K }>;
  if (pr.player !== player) fail('not_your_decision', 'A decisão não é sua');
  return pr;
}

// ---------------------------------------------------------------------------
// Dados e Ilha

function rollDice(s: GameState, p: Player, forced?: number[]): Roll {
  const busaoIdx = s.effects.findIndex((e) => e.kind === 'busao' && e.player === p.id);
  const oneDie = busaoIdx >= 0;
  if (oneDie) s.effects.splice(busaoIdx, 1);
  let dice: number[];
  if (forced) {
    if (!s.allowForcedDice) fail('forced_dice', 'Dados forçados não são permitidos');
    dice = oneDie ? forced.slice(0, 1) : forced.slice(0, 2);
  } else {
    dice = oneDie ? [nextInt(s, 1, 6)] : [nextInt(s, 1, 6), nextInt(s, 1, 6)];
  }
  const doubles = dice.length === 2 && dice[0] === dice[1];
  return { dice, total: dice.reduce((a, b) => a + b, 0), doubles };
}

function onRoll(ctx: Ctx, playerId: PlayerId, forced?: number[]): void {
  const s = ctx.s;
  const p = requireCurrent(s, playerId);
  requireFree(s);
  if (s.phase !== 'roll') fail('wrong_phase', 'Agora não é hora de rolar os dados');

  const roll = rollDice(s, p, forced);
  s.lastRoll = roll;
  emit(ctx, { type: 'diceRolled', player: p.id, roll });

  if (p.inJail) {
    // Sonegador não sai com dupla: cumpre a pena (ou paga a fiança tripla).
    if (roll.doubles && !p.evader) {
      leaveJail(ctx, p.id, 'doubles');
      s.extraRoll = false;
      moveAndQueue(ctx, p, roll.total);
      return;
    }
    p.jailAttempts += 1;
    emit(ctx, { type: 'jailRollFailed', player: p.id, attempts: p.jailAttempts });
    if (p.jailAttempts >= (p.evader ? EVADE_JAIL_ATTEMPTS : MAX_JAIL_ATTEMPTS)) {
      // Esperou as 3 vezes: sai de graça e anda com este lance.
      leaveJail(ctx, p.id, 'forced');
      s.extraRoll = false;
      moveAndQueue(ctx, p, roll.total);
      return;
    }
    s.phase = 'endTurn';
    return;
  }

  if (roll.doubles) {
    s.doublesInRow += 1;
    if (s.doublesInRow >= 3) {
      s.phase = 'resolving';
      sendToJail(ctx, p.id, 'tresDuplas');
      return;
    }
    s.extraRoll = true;
  } else {
    s.extraRoll = false;
  }
  moveAndQueue(ctx, p, roll.total);
}

function moveAndQueue(ctx: Ctx, p: Player, steps: number): void {
  const s = ctx.s;
  s.phase = 'resolving';
  moveForward(ctx, p.id, steps);
  s.queue.push({ k: 'magnet', player: p.id }, { k: 'land', player: p.id });
}

function onPayJail(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const p = requireCurrent(s, playerId);
  requireFree(s);
  if (s.phase !== 'roll' || !p.inJail) fail('not_in_jail', 'Você não está na Ilha');
  const fine = jailFine(p);
  if (p.cash < fine) fail('no_cash', 'Dinheiro insuficiente');
  transfer(ctx, p.id, 'bank', fine, 'cadeia');
  emit(ctx, { type: 'paid', from: p.id, to: 'bank', amount: fine, reason: 'cadeia' });
  leaveJail(ctx, p.id, 'paid');
}

// ---------------------------------------------------------------------------
// Invasão do MST

/** Cidades com casa (1 a 3) paradas há MST_IDLE_ROUNDS rodadas, fora das protegidas. */
export function mstTargets(s: GameState): number[] {
  const out: number[] = [];
  s.props.forEach((o, i) => {
    if (!o || square(i).kind !== 'city' || o.level < 1 || o.level >= HOTEL_LEVEL) return;
    if ((s.farms ?? []).includes(i)) return;
    const since = s.lastVisit?.[i] ?? s.round;
    if (s.round - since >= MST_IDLE_ROUNDS) out.push(i);
  });
  return out;
}

/** Quantas rodadas faltam para o MST chegar nesta cidade (null = não corre risco). */
export function mstRoundsLeft(s: Pick<GameState, 'props' | 'round' | 'lastVisit' | 'farms'>, i: number): number | null {
  const o = s.props[i];
  if (!o || square(i).kind !== 'city' || o.level < 1 || o.level >= HOTEL_LEVEL || (s.farms ?? []).includes(i)) return null;
  const since = s.lastVisit?.[i] ?? s.round;
  return Math.max(0, MST_IDLE_ROUNDS - (s.round - since));
}

function queueMst(s: GameState): void {
  for (const i of mstTargets(s)) s.queue.push({ k: 'mst', player: s.props[i]!.owner, square: i });
}

function mstArrive(ctx: Ctx, owner: PlayerId, index: number): void {
  const s = ctx.s;
  const o = s.props[index];
  if (!o || o.owner !== owner || !mstTargets(s).includes(index)) return;
  emit(ctx, { type: 'mstArrived', owner, square: index });
  // Carta Fazendeiro comprada na lojinha: expulsa de graça.
  if (useHeld(ctx, owner, 'fazendeiro')) {
    touch(s, index);
    emit(ctx, { type: 'mstExpelled', owner, square: index, paid: 0 });
    return;
  }
  if (getPlayer(s, owner).cash >= MST_EXPEL_COST) s.prompt = { kind: 'mst', player: owner, square: index, cost: MST_EXPEL_COST };
  else mstDowngrade(ctx, owner, index);
}

function mstDowngrade(ctx: Ctx, owner: PlayerId, index: number): void {
  const o = ctx.s.props[index]!;
  const from = o.level;
  o.level = Math.max(0, from - 1) as Level;
  touch(ctx.s, index);
  emit(ctx, { type: 'mstInvaded', owner, square: index, from, to: o.level });
}

// ---------------------------------------------------------------------------
// Lojinha

function onBuyCard(ctx: Ctx, playerId: PlayerId, card: HeldCard): void {
  const s = ctx.s;
  const p = requireCurrent(s, playerId);
  requireFree(s);
  if (s.phase !== 'roll') fail('wrong_phase', 'A lojinha abre na sua vez, antes de rolar os dados');
  const price = (SHOP as Record<string, number>)[card];
  if (price === undefined) fail('bad_card', 'Essa carta não está à venda');
  if (handSize(p) >= HAND_LIMIT) fail('hand_full', `Mão cheia: no máximo ${HAND_LIMIT} cartas`);
  if (p.cash < price!) fail('no_cash', 'Dinheiro insuficiente');
  transfer(ctx, p.id, 'bank', price!, 'lojinha');
  (p.shop ??= []).push(card);
  emit(ctx, { type: 'cardBought', player: p.id, card, price: price! });
}

function onExpelMst(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'mst', playerId);
  const p = getPlayer(s, playerId);
  if (p.cash < pr.cost) fail('no_cash', 'Dinheiro insuficiente');
  s.prompt = null;
  transfer(ctx, playerId, 'bank', pr.cost, 'fazendeiro');
  emit(ctx, { type: 'paid', from: playerId, to: 'bank', amount: pr.cost, reason: 'fazendeiro' });
  touch(s, pr.square);
  emit(ctx, { type: 'mstExpelled', owner: playerId, square: pr.square, paid: pr.cost });
}

function onAcceptMst(ctx: Ctx, playerId: PlayerId): void {
  const pr = requirePrompt(ctx.s, 'mst', playerId);
  ctx.s.prompt = null;
  mstDowngrade(ctx, playerId, pr.square);
}

/** Fiança para sair da Ilha: tripla para quem foi pego sonegando. */
export function jailFine(p: Pick<Player, 'evader'>): number {
  return p.evader ? JAIL_FINE * EVADE_JAIL_FINE_MULT : JAIL_FINE;
}

function onPayTax(ctx: Ctx, playerId: PlayerId): void {
  const pr = requirePrompt(ctx.s, 'tax', playerId);
  ctx.s.prompt = null;
  charge(ctx, playerId, 'bank', pr.amount, 'imposto');
}

function onEvadeTax(ctx: Ctx, playerId: PlayerId, forced?: number[]): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'tax', playerId);
  s.prompt = null;
  let dice: number[];
  if (forced) {
    if (!s.allowForcedDice) fail('forced_dice', 'Dados forçados não são permitidos');
    dice = forced.slice(0, 2);
  } else dice = [nextInt(s, 1, 6), nextInt(s, 1, 6)];
  const total = dice.reduce((a, b) => a + b, 0);
  const success = total >= EVADE_MIN_TOTAL;
  const amount = success ? pr.evaded : pr.caught;
  emit(ctx, { type: 'taxEvasion', player: playerId, dice, total, success, amount });
  if (!success) sendToJail(ctx, playerId, 'sonegacao');
  if (amount > 0) charge(ctx, playerId, 'bank', amount, success ? 'imposto' : 'sonegacao');
}

function onUseJailCard(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const p = requireCurrent(s, playerId);
  requireFree(s);
  if (s.phase !== 'roll' || !p.inJail) fail('not_in_jail', 'Você não está na Ilha');
  if (!useHeld(ctx, p.id, 'advogado')) fail('no_card', 'Você não tem o Advogado');
  leaveJail(ctx, p.id, 'card');
}

// ---------------------------------------------------------------------------
// Compra, construção e compra de cidade alheia

/** Quanto custa construir até `level` agora (com o desconto da Black Friday, se tiver). */
export function buildQuote(s: GameState, playerId: PlayerId, index: number, level: number): { cost: number; discounted: boolean } {
  const o = s.props[index];
  const from = o ? o.level : -1;
  const full = upgradeCost(index, from, level);
  const p = getPlayer(s, playerId);
  // Black Friday: a construção (não o terreno) sai pela metade.
  if (hasCard(p, 'blackFriday') && level > Math.max(from, 0)) {
    const land = from < 0 ? levelCost(index, 0) : 0;
    return { cost: land + Math.ceil((full - land) / 2), discounted: true };
  }
  return { cost: full, discounted: false };
}

function onBuild(ctx: Ctx, playerId: PlayerId, level: number): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'build', playerId);
  if (!Number.isInteger(level) || level <= pr.from || level > pr.max) fail('bad_level', 'Nível inválido');
  const { cost, discounted } = buildQuote(s, playerId, pr.square, level);
  if (getPlayer(s, playerId).cash < cost) fail('no_cash', 'Dinheiro insuficiente');
  if (discounted) useHeld(ctx, playerId, 'blackFriday');
  transfer(ctx, playerId, 'bank', cost, pr.from < 0 ? 'compra' : 'construcao');
  s.prompt = null;
  if (pr.from < 0) setOwner(ctx, playerId, pr.square, level as Level);
  else s.props[pr.square]!.level = level as Level;
  touch(s, pr.square);
  emit(ctx, { type: 'built', player: playerId, square: pr.square, from: pr.from, level: level as Level, cost });
  if (level === HOTEL_LEVEL) {
    emit(ctx, { type: 'hotelBuilt', player: playerId, square: pr.square, firstOfGame: !s.firstHotelDone });
    s.firstHotelDone = true;
  }
}

function onSkipBuild(ctx: Ctx, playerId: PlayerId): void {
  requirePrompt(ctx.s, 'build', playerId);
  ctx.s.prompt = null;
}

function onTakeover(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'takeover', playerId);
  const o = s.props[pr.square];
  if (!o || o.owner === playerId) fail('bad_takeover', 'Não dá para comprar esta cidade');
  const price = takeoverPrice(s, pr.square);
  const buyer = getPlayer(s, playerId);
  if (buyer.cash < price) fail('no_cash', 'Dinheiro insuficiente');
  const from = o!.owner;
  transfer(ctx, playerId, from, price, 'compraAlheia');
  s.prompt = null;
  setOwner(ctx, playerId, pr.square);
  buyer.stats.takeoversMade += 1;
  getPlayer(s, from).stats.takeoversSuffered += 1;
  emit(ctx, { type: 'takeover', buyer: playerId, from, square: pr.square, price });
  // Depois de comprar, já pode construir na cidade, como se tivesse caído nela.
  s.queue.unshift({ k: 'offerBuild', player: playerId, square: pr.square });
}

function onSkipTakeover(ctx: Ctx, playerId: PlayerId): void {
  requirePrompt(ctx.s, 'takeover', playerId);
  ctx.s.prompt = null;
}

// ---------------------------------------------------------------------------
// Campeonato Mundial e Volta ao Mundo

function onHost(ctx: Ctx, playerId: PlayerId, index: number): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'championship', playerId);
  if (!pr.options.includes(index)) fail('bad_target', 'Cidade inválida');
  const same = s.championship?.square === index;
  const mult = same ? Math.min(CHAMPIONSHIP_MAX_MULT, s.championship!.mult + 1) : CHAMPIONSHIP_START_MULT;
  s.championship = { square: index, mult };
  s.prompt = null;
  emit(ctx, { type: 'championship', player: playerId, square: index, mult });
}

/** Para onde a Volta ao Mundo pode levar: cidades livres ou suas, e resorts. */
export function tourOptions(s: GameState, playerId: PlayerId): number[] {
  const p = getPlayer(s, playerId);
  return OWNABLE_SQUARES.filter((i) => {
    if (i === p.position) return false;
    const o = s.props[i];
    return !o || o.owner === playerId;
  });
}

function onTour(ctx: Ctx, playerId: PlayerId, to: number): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'tour', playerId);
  if (!pr.options.includes(to)) fail('bad_target', 'Destino inválido');
  const p = getPlayer(s, playerId);
  if (p.cash < WORLD_TOUR_COST) fail('no_cash', 'Dinheiro insuficiente');
  transfer(ctx, playerId, 'bank', WORLD_TOUR_COST, 'viagem');
  s.prompt = null;
  p.worldTour = false;
  const from = p.position;
  moveForwardTo(ctx, playerId, to);
  emit(ctx, { type: 'traveled', player: playerId, from, to, cost: WORLD_TOUR_COST });
  // A viagem substitui os dados nesta vez.
  s.extraRoll = false;
  s.queue.unshift({ k: 'land', player: playerId });
}

function onSkipTour(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  requirePrompt(s, 'tour', playerId);
  s.prompt = null;
  getPlayer(s, playerId).worldTour = false;
  s.phase = 'roll';
}

function onChoose(ctx: Ctx, playerId: PlayerId, choice: string): void {
  const s = ctx.s;
  const pr = requirePrompt(s, 'card', playerId);
  if (choice === 'skip' && !pr.canSkip) fail('cannot_skip', 'Esta carta não pode ser pulada');
  if (choice !== 'skip' && !pr.choices.some((c) => c.id === choice)) fail('bad_choice', 'Escolha inválida');
  applyCardChoice(ctx, pr, choice);
}

// ---------------------------------------------------------------------------
// Dívidas, venda e falência

function requireDebtor(s: GameState, playerId: PlayerId) {
  const debt = s.debts[0];
  if (!debt) fail('no_debt', 'Não há dívida em aberto');
  if (debt!.debtor !== playerId) fail('not_your_debt', 'A dívida não é sua');
  return debt!;
}

/** Quanto o banco paga por esta propriedade agora (metade do valor investido). */
export function sellValue(s: GameState, index: number): number {
  const o = s.props[index];
  return o ? Math.floor(valueOf(index, o.level) * SELL_RATE) : 0;
}

function onSell(ctx: Ctx, playerId: PlayerId, index: number): void {
  const s = ctx.s;
  requireDebtor(s, playerId);
  const o = s.props[index];
  if (!o || o.owner !== playerId) fail('not_yours', 'Esta propriedade não é sua');
  sellToBank(ctx, playerId, index);
}

function sellToBank(ctx: Ctx, playerId: PlayerId, index: number): void {
  const s = ctx.s;
  const amount = sellValue(s, index);
  s.props[index] = null;
  if (s.championship?.square === index) s.championship = null;
  credit(ctx, playerId, amount, 'venda');
  emit(ctx, { type: 'propertySold', player: playerId, square: index, amount });
}

function onPayDebt(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const debt = requireDebtor(s, playerId);
  if (getPlayer(s, playerId).cash < debt.amount) fail('no_cash', 'Dinheiro insuficiente');
  transfer(ctx, playerId, debt.creditor, debt.amount, debt.reason);
  emit(ctx, { type: 'paid', from: playerId, to: debt.creditor, amount: debt.amount, reason: debt.reason });
  s.debts.shift();
}

/** Quanto o jogador consegue levantar vendendo tudo ao banco. */
export function liquidationValue(s: GameState, playerId: PlayerId): number {
  return ownedBy(s, playerId).reduce((t, i) => t + sellValue(s, i), 0);
}

/**
 * Falência: vende tudo ao banco, entrega ao credor o que tiver (o resto é perdoado)
 * e sai da partida. As propriedades voltam ao banco, como no Business Tour.
 */
function onBankruptcy(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const debt = requireDebtor(s, playerId);
  const p = getPlayer(s, playerId);
  for (const i of ownedBy(s, playerId)) sellToBank(ctx, playerId, i);
  const paid = Math.min(p.cash, debt.amount);
  transfer(ctx, playerId, debt.creditor, paid, debt.reason);
  s.debts.shift();
  s.bankruptcyHappened = true;
  emit(ctx, { type: 'bankrupt', player: playerId, creditor: debt.creditor, paid, forgiven: debt.amount - paid });
  eliminate(ctx, p);
}

function eliminate(ctx: Ctx, p: Player): void {
  const s = ctx.s;
  p.eliminated = true;
  p.cash = 0;
  p.inJail = false;
  p.worldTour = false;
  for (const c of p.held) s.discard.push(c);
  p.held = [];
  delete p.shop;
  s.debts = s.debts.filter((d) => d.debtor !== p.id);
  for (const d of s.debts) if (d.creditor === p.id) d.creditor = 'bank';
  s.queue = s.queue.filter((st) => st.player !== p.id);
  s.trades = s.trades.filter((t) => t.from !== p.id && t.to !== p.id);
  if (s.prompt && 'player' in s.prompt && s.prompt.player === p.id) {
    if (s.prompt.kind === 'card' && s.pendingCard) {
      s.discard.push(s.pendingCard);
      s.pendingCard = null;
    }
    s.prompt = null;
  }
  // Se era a vez dele, a vez passa para o próximo na hora.
  if (currentPlayer(s).id === p.id && s.phase !== 'finished') {
    s.queue = [];
    s.extraRoll = false;
    s.prompt = null;
    if (activePlayers(s).length > 1) nextTurn(ctx);
  }
}

/**
 * Resolve a dívida automaticamente (usado no fim do cronômetro e pelos bots):
 * se vender for suficiente, vende o mínimo (das mais baratas e fora de grupo completo primeiro) e paga;
 * senão, quebra.
 */
function onAutoSettle(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  const debt = requireDebtor(s, playerId);
  const p = getPlayer(s, playerId);
  if (p.cash + liquidationValue(s, playerId) < debt.amount) {
    onBankruptcy(ctx, playerId);
    return;
  }
  for (let guard = 0; guard < 64 && p.cash < debt.amount; guard++) {
    const sellable = ownedBy(s, playerId).sort(
      (a, b) => fullGroupRank(s, playerId, a) - fullGroupRank(s, playerId, b) || sellValue(s, a) - sellValue(s, b) || a - b,
    );
    const target = sellable[0];
    if (target === undefined) break;
    sellToBank(ctx, playerId, target);
  }
  if (p.cash >= debt.amount) onPayDebt(ctx, playerId);
  else onBankruptcy(ctx, playerId);
}

function fullGroupRank(s: GameState, playerId: PlayerId, index: number): number {
  const sq = square(index);
  if (sq.kind === 'resort') return 2;
  const g = GROUPS[sq.group!]!;
  return g.every((i) => s.props[i]?.owner === playerId) ? 1 : 0;
}

// ---------------------------------------------------------------------------
// Trocas (o Business Tour não tem; ficam no motor, mas a tela não oferece)

function validateOffer(s: GameState, owner: PlayerId, offer: TradeOffer): void {
  const p = getPlayer(s, owner);
  if (!Number.isInteger(offer.cash) || offer.cash < 0) fail('bad_trade', 'Valor de troca inválido');
  if (offer.cash > p.cash) fail('bad_trade', 'Dinheiro insuficiente para a troca');
  if (new Set(offer.squares).size !== offer.squares.length) fail('bad_trade', 'Propriedade repetida');
  for (const i of offer.squares) {
    if (!OWNABLE_SQUARES.includes(i)) fail('bad_trade', 'Casa não negociável');
    if (s.props[i]?.owner !== owner) fail('bad_trade', 'Propriedade não pertence a quem oferece');
  }
  const held = [...p.held];
  for (const c of offer.cards) {
    const k = held.indexOf(c);
    if (k < 0) fail('bad_trade', 'Carta não está na mão de quem oferece');
    held.splice(k, 1);
  }
}

function onProposeTrade(ctx: Ctx, from: PlayerId, to: PlayerId, give: TradeOffer, get: TradeOffer): void {
  const s = ctx.s;
  const a = getPlayer(s, from);
  const b = getPlayer(s, to);
  if (a.eliminated || b.eliminated) fail('bad_trade', 'Jogador fora da partida');
  if (from === to) fail('bad_trade', 'Não dá para trocar consigo mesmo');
  if (s.trades.some((t) => t.from === from)) fail('trade_pending', 'Você já tem uma proposta em aberto');
  const empty = (o: TradeOffer) => o.cash === 0 && o.squares.length === 0 && o.cards.length === 0;
  if (empty(give) && empty(get)) fail('bad_trade', 'Proposta vazia');
  validateOffer(s, from, give);
  validateOffer(s, to, get);
  const trade = { id: s.nextTradeId++, from, to, give: cloneOffer(give), get: cloneOffer(get) };
  s.trades.push(trade);
  emit(ctx, { type: 'tradeProposed', trade });
}

const cloneOffer = (o: TradeOffer): TradeOffer => ({ cash: o.cash, squares: [...o.squares], cards: [...o.cards] });

function findTrade(s: GameState, id: number) {
  const t = s.trades.find((x) => x.id === id);
  if (!t) fail('no_trade', 'Proposta não encontrada');
  return t!;
}

function onAcceptTrade(ctx: Ctx, playerId: PlayerId, id: number): void {
  const s = ctx.s;
  const t = findTrade(s, id);
  if (t.to !== playerId) fail('not_your_trade', 'A proposta não é para você');
  validateOffer(s, t.from, t.give);
  validateOffer(s, t.to, t.get);
  const a = getPlayer(s, t.from);
  const b = getPlayer(s, t.to);
  if (handSize(a) - t.give.cards.length + t.get.cards.length > HAND_LIMIT) fail('hand_full', 'Mão cheia');
  if (handSize(b) - t.get.cards.length + t.give.cards.length > HAND_LIMIT) fail('hand_full', 'Mão cheia');
  moveOffer(ctx, t.from, t.to, t.give);
  moveOffer(ctx, t.to, t.from, t.get);
  s.trades = s.trades.filter((x) => x.id !== id);
  emit(ctx, { type: 'tradeAccepted', trade: t });
}

function moveOffer(ctx: Ctx, from: PlayerId, to: PlayerId, offer: TradeOffer): void {
  const s = ctx.s;
  if (offer.cash > 0) transfer(ctx, from, to, offer.cash, 'troca');
  for (const i of offer.squares) setOwner(ctx, to, i);
  const a = getPlayer(s, from);
  const b = getPlayer(s, to);
  for (const c of offer.cards) {
    a.held.splice(a.held.indexOf(c), 1);
    b.held.push(c as HeldCard);
  }
}

function onRejectTrade(ctx: Ctx, playerId: PlayerId, id: number): void {
  const s = ctx.s;
  const t = findTrade(s, id);
  if (t.to !== playerId) fail('not_your_trade', 'A proposta não é para você');
  s.trades = s.trades.filter((x) => x.id !== id);
  emit(ctx, { type: 'tradeRejected', tradeId: id, by: playerId });
}

function onCancelTrade(ctx: Ctx, playerId: PlayerId, id: number): void {
  const s = ctx.s;
  const t = findTrade(s, id);
  if (t.from !== playerId) fail('not_your_trade', 'A proposta não é sua');
  s.trades = s.trades.filter((x) => x.id !== id);
  emit(ctx, { type: 'tradeCancelled', tradeId: id });
}

// ---------------------------------------------------------------------------
// Fim do turno

function onEndTurn(ctx: Ctx, playerId: PlayerId): void {
  const s = ctx.s;
  requireCurrent(s, playerId);
  requireFree(s);
  if (s.phase !== 'endTurn') fail('wrong_phase', 'Ainda há coisas para fazer neste turno');
  nextTurn(ctx);
}

/** Passa a vez para o próximo jogador que ainda está na partida. */
function nextTurn(ctx: Ctx): void {
  const s = ctx.s;
  const n = s.players.length;
  let next = s.current;
  let wrapped = false;
  for (let k = 0; k < n; k++) {
    next = (next + 1) % n;
    if (next === 0) wrapped = true;
    if (!s.players[next]!.eliminated) break;
  }
  s.current = next;
  s.turnCounter += 1;
  s.doublesInRow = 0;
  s.extraRoll = false;
  s.lastRoll = null;
  if (wrapped) {
    if (s.finalRound) {
      endGame(ctx, 'time');
      return;
    }
    s.round += 1;
    s.roundStarts.push(s.elapsedMs);
    maybeStartFinalRound(ctx);
    queueMst(s);
  }
  s.effects = s.effects.filter((e) => !('untilTurn' in e) || s.turnCounter < e.untilTurn);
  s.phase = 'roll';
  const p = currentPlayer(s);
  emit(ctx, { type: 'turnStarted', player: p.id, turn: s.turnCounter, round: s.round });
  // Volta ao Mundo: antes dos dados, escolhe para onde voar (ou rola normal).
  if (p.worldTour && !p.inJail) {
    const options = tourOptions(s, p.id);
    if (options.length > 0 && p.cash >= WORLD_TOUR_COST) {
      s.phase = 'resolving';
      s.prompt = { kind: 'tour', player: p.id, options };
    } else {
      p.worldTour = false;
    }
  }
}

// ===========================================================================
// Consultas para cliente, servidor e bots

/** Quem precisa agir agora e qual decisão está aberta. */
export function pendingDecision(s: GameState):
  | { kind: 'debt'; player: PlayerId; amount: number; creditor: string; reason: string }
  | { kind: 'prompt'; prompt: Prompt }
  | { kind: 'turn'; player: PlayerId; phase: 'roll' | 'endTurn' }
  | { kind: 'none' } {
  if (s.phase === 'finished') return { kind: 'none' };
  const d = s.debts[0];
  if (d) return { kind: 'debt', player: d.debtor, amount: d.amount, creditor: d.creditor, reason: d.reason };
  if (s.prompt) return { kind: 'prompt', prompt: s.prompt };
  if (s.phase === 'roll' || s.phase === 'endTurn') return { kind: 'turn', player: currentPlayer(s).id, phase: s.phase };
  return { kind: 'none' };
}

/**
 * Ação padrão quando o cronômetro estoura (o servidor usa isto):
 * não compra, não constrói, não toma cidade, não viaja; Campeonato vai para a primeira cidade;
 * dívidas no automático.
 */
export function timeoutAction(s: GameState): Action | null {
  const d = pendingDecision(s);
  switch (d.kind) {
    case 'debt':
      return { type: 'AUTO_SETTLE_DEBT', player: d.player };
    case 'prompt': {
      const pr = d.prompt;
      if (pr.kind === 'build') return { type: 'SKIP_BUILD', player: pr.player };
      if (pr.kind === 'takeover') return { type: 'SKIP_TAKEOVER', player: pr.player };
      if (pr.kind === 'tour') return { type: 'SKIP_TOUR', player: pr.player };
      if (pr.kind === 'championship') return { type: 'HOST', player: pr.player, square: pr.options[0]! };
      if (pr.kind === 'tax') return { type: 'PAY_TAX', player: pr.player };
      if (pr.kind === 'mst') return { type: 'ACCEPT_MST', player: pr.player };
      return { type: 'CHOOSE', player: pr.player, choice: pr.canSkip ? 'skip' : pr.choices[0]!.id };
    }
    case 'turn':
      return d.phase === 'roll' ? { type: 'ROLL', player: d.player } : { type: 'END_TURN', player: d.player };
    default:
      return null;
  }
}

export type PublicState = Omit<GameState, 'rng' | 'deck'> & { deckCount: number };

/**
 * Versão do estado que pode ir para os jogadores: sem o gerador de dados
 * e sem a ordem do baralho (senão daria para prever dados e cartas).
 */
export function publicView(s: GameState): PublicState {
  const { rng: _rng, deck, ...rest } = s;
  return { ...structuredClone(rest), deckCount: deck.length };
}

export { GROUPS };
